// ParoDetalleDrawer — drill de paros del día de una línea (pantalla del
// técnico /mantenimiento/lineas) + acciones sobre cada paro:
//   · Cerrar paro abierto (hora backdateable en el marco fake-UTC del tenant)
//   · Revisar paro (flujo UNIFICADO, requisito 2026-07-06): en un solo modal
//     el técnico decide si el paro está correcto (sello vía validar) o si
//     necesita corrección (mismo formulario de causal en cascada + horas)
//   · Proponer corrección directa sobre paros YA validados
//   · Generar/ligar orden de trabajo
//
// Los timestamps de paros viajan en "fake-UTC": pared local del tenant
// etiquetada Z. Se FORMATEAN siempre con timeZone:'UTC' (muestra la pared
// guardada) y se COMPONEN sin conversión de zona (YYYY-MM-DDTHH:MM:00Z).
//
// Exporta también EstadoParoMttoBadge, ParoAcciones y los formateadores para
// que la cola de validación de LineasParosMttoPage reuse las mismas piezas.

import { useState } from 'react';
import { Link } from 'react-router-dom';
import { toast } from 'sonner';
import {
  correccionParoProponerSchema,
  type CorreccionParoProponer,
  type EstadoParoMtto,
  type ParoMttoItem,
  type ParoOrdenTrabajoInput,
} from 'schemas';
import { Badge } from '../../components/ui/badge';
import { Button } from '../../components/ui/button';
import { Card, CardContent } from '../../components/ui/card';
import { Chip } from '../../components/ui/chip';
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../../components/ui/dialog';
import { Drawer, DrawerBody, DrawerHeader, DrawerTitle } from '../../components/ui/drawer';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { SegmentedControl } from '../../components/ui/segmented-control';
import { Select } from '../../components/ui/select';
import { Spinner } from '../../components/ui/spinner';
import { Textarea } from '../../components/ui/textarea';
import { useCausalesParo } from '../../lib/api/queries';
import { cn } from '../../lib/utils';
import { CargandoModulo, ErrorModulo } from './MantenimientoShell';
import {
  useActivosMtto,
  useCerrarParoMtto,
  useGenerarOtDesdeParo,
  useLineaParosDia,
  useOrdenesMtto,
  useProponerCorreccionParo,
  useValidarParoMtto,
} from './queries';
import { esCodigoApi, fmtFecha, mensajeError, usePermisosMtto } from './shared';

// ---------------------------------------------------------------------------
// Fake-UTC: formateo y composición (SIN conversión de zona)
// ---------------------------------------------------------------------------

/** "HH:MM" de la pared guardada (fake-UTC → SIEMPRE timeZone:'UTC'). */
export function fmtHoraParo(iso: string | null): string {
  if (!iso) return '—';
  return new Date(iso).toLocaleTimeString('es-CO', {
    hour: '2-digit',
    minute: '2-digit',
    timeZone: 'UTC',
  });
}

/** "DD/MM/AA HH:MM" de la pared guardada (fake-UTC). */
export function fmtFechaHoraParo(iso: string | null): string {
  if (!iso) return '—';
  return new Date(iso).toLocaleString('es-CO', {
    year: '2-digit',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    timeZone: 'UTC',
  });
}

/** ISO fake-UTC → valor de <input type="datetime-local"> ("YYYY-MM-DDTHH:MM"). */
function wallFromIso(iso: string): string {
  return new Date(iso).toISOString().slice(0, 16);
}

/**
 * Pared elegida → ISO fake-UTC (se emite la pared TAL CUAL, etiquetada Z).
 * Exportada para que la corrección del SUPERVISOR (features/supervisor) use la
 * MISMA conversión: la sutileza fake-UTC no se replica, se comparte.
 */
export function isoFromWall(wall: string): string {
  return `${wall.slice(0, 16)}:00Z`;
}

/** "YYYY-MM-DDTHH:MM" del AHORA local del dispositivo (pared local). */
function nowWall(): string {
  const d = new Date();
  const p = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}T${p(d.getHours())}:${p(d.getMinutes())}`;
}

/** Minutos enteros entre dos ISO fake-UTC (mismo marco → resta directa). */
export function minutosEntre(inicioIso: string, finIso: string): number {
  return Math.round((Date.parse(finIso) - Date.parse(inicioIso)) / 60_000);
}

/** "45 min" / "2 h 05 m" para duraciones de paro. */
export function fmtMinutosParo(min: number | null): string {
  if (min === null) return '—';
  const m = Math.round(min);
  if (m < 60) return `${m} min`;
  const h = Math.floor(m / 60);
  const resto = m % 60;
  return resto === 0 ? `${h} h` : `${h} h ${String(resto).padStart(2, '0')} m`;
}

// ---------------------------------------------------------------------------
// Estado derivado del paro → badge
// ---------------------------------------------------------------------------

export const ESTADO_PARO_MTTO_LABEL: Record<EstadoParoMtto, string> = {
  abierto: 'Abierto',
  pendiente: 'Pendiente',
  correccion_propuesta: 'Corrección propuesta',
  validado: 'Validado',
  validado_corregido: 'Validado corregido',
};

const ESTADO_PARO_MTTO_TONE: Record<EstadoParoMtto, 'crit' | 'warn' | 'info' | 'ok'> = {
  abierto: 'crit',
  pendiente: 'warn',
  correccion_propuesta: 'info',
  validado: 'ok',
  validado_corregido: 'ok',
};

export function EstadoParoMttoBadge({ estado }: { estado: EstadoParoMtto }) {
  return (
    <Badge tone={ESTADO_PARO_MTTO_TONE[estado]} dot>
      {ESTADO_PARO_MTTO_LABEL[estado]}
    </Badge>
  );
}

// ---------------------------------------------------------------------------
// Acciones por paro (drawer + cola de validación)
// ---------------------------------------------------------------------------

type ModalActivo = 'cerrar' | 'revisar' | 'corregir' | 'ot' | null;

export function ParoAcciones({ paro, puedeOperar }: { paro: ParoMttoItem; puedeOperar: boolean }) {
  const [modal, setModal] = useState<ModalActivo>(null);

  const cerrado = paro.hora_fin !== null;
  const yaValidado = paro.estado_mtto === 'validado' || paro.estado_mtto === 'validado_corregido';

  return (
    <div className="flex flex-wrap items-center gap-1.5">
      {paro.orden_trabajo_id !== null ? (
        <Link to={`/mantenimiento/ordenes/${paro.orden_trabajo_id}`}>
          <Chip tone="accent" interactive className="px-2 py-0.5 text-[11px]">
            Ver OT →
          </Chip>
        </Link>
      ) : null}

      {!puedeOperar ? null : (
        <>
          {paro.estado_mtto === 'abierto' ? (
            <Button size="sm" onClick={() => setModal('cerrar')}>
              Cerrar paro
            </Button>
          ) : null}

          {/* Flujo unificado: cerrado sin sello → revisar (validar O corregir
              en el mismo modal). Con corrección viva no se re-revisa: la
              propuesta ya se muestra en la card/fila. */}
          {paro.estado_mtto === 'pendiente' ? (
            <Button size="sm" onClick={() => setModal('revisar')}>
              Revisar paro
            </Button>
          ) : null}

          {/* Paros YA validados: corrección directa (el sello no los blinda). */}
          {cerrado && yaValidado && paro.correccion_viva === null ? (
            <Button size="sm" variant="outline" onClick={() => setModal('corregir')}>
              Proponer corrección
            </Button>
          ) : null}

          {paro.orden_trabajo_id === null ? (
            <Button size="sm" variant="outline" onClick={() => setModal('ot')}>
              Generar OT
            </Button>
          ) : null}
        </>
      )}

      {/* Montados solo al abrir: evita disparar las queries de los modales
          (órdenes abiertas / activos) por cada fila de la tabla. */}
      {modal === 'cerrar' ? (
        <CerrarParoMttoModal paro={paro} onClose={() => setModal(null)} />
      ) : null}
      {modal === 'revisar' ? <RevisarParoModal paro={paro} onClose={() => setModal(null)} /> : null}
      {modal === 'corregir' ? (
        <ProponerCorreccionModal paro={paro} onClose={() => setModal(null)} />
      ) : null}
      {modal === 'ot' ? <GenerarOtModal paro={paro} onClose={() => setModal(null)} /> : null}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Resumen compartido del paro (cabecera de los modales)
// ---------------------------------------------------------------------------

function ResumenParo({ paro }: { paro: ParoMttoItem }) {
  return (
    <div className="rounded bg-surface-overlay p-2 text-xs text-fg-secondary">
      <strong className="text-fg">
        {paro.causal_madre.nombre} → {paro.causal.nombre}
      </strong>
      <br />
      {paro.linea_nombre} · {fmtFecha(paro.fecha)} · {fmtHoraParo(paro.hora_inicio)} —{' '}
      {paro.hora_fin === null ? 'en curso' : fmtHoraParo(paro.hora_fin)} ·{' '}
      {fmtMinutosParo(paro.minutos_perdidos)}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Modal: cerrar paro abierto (hora backdateable, fake-UTC)
// ---------------------------------------------------------------------------

function CerrarParoMttoModal({ paro, onClose }: { paro: ParoMttoItem; onClose: () => void }) {
  const cerrar = useCerrarParoMtto();
  const [horaFinWall, setHoraFinWall] = useState(nowWall);
  const [observaciones, setObservaciones] = useState('');
  const [error, setError] = useState<string | null>(null);

  // Mínimo: inicio del paro + 1 minuto (misma pared fake-UTC del registro).
  const minWall = (() => {
    const d = new Date(paro.hora_inicio);
    d.setUTCMinutes(d.getUTCMinutes() + 1);
    return wallFromIso(d.toISOString());
  })();

  async function handleCerrar() {
    setError(null);
    if (!horaFinWall) {
      setError('Elija la hora de cierre');
      return;
    }
    if (horaFinWall < minWall) {
      setError('Debe ser posterior al inicio del paro');
      return;
    }
    if (horaFinWall > nowWall()) {
      setError('La hora de cierre no puede estar en el futuro');
      return;
    }
    const obs = observaciones.trim();
    if (obs.length > 0 && obs.length < 5) {
      setError('La observación debe tener mínimo 5 caracteres');
      return;
    }
    try {
      const res = await cerrar.mutateAsync({
        paroId: paro.id,
        input: { hora_fin: isoFromWall(horaFinWall), observaciones: obs.length > 0 ? obs : null },
      });
      toast.success(`Paro cerrado · ${fmtMinutosParo(res.minutos_perdidos)}`);
      onClose();
    } catch (err) {
      if (esCodigoApi(err, 'hora_fin_futura')) {
        toast.error('La hora de cierre no puede estar en el futuro');
        return;
      }
      if (esCodigoApi(err, 'invalid_hora_fin')) {
        toast.error('Debe ser posterior al inicio del paro');
        return;
      }
      toast.error(mensajeError(err));
    }
  }

  return (
    <Dialog open onClose={onClose}>
      <DialogHeader>
        <DialogTitle>Cerrar paro</DialogTitle>
      </DialogHeader>
      <DialogContent className="flex flex-col gap-3">
        <ResumenParo paro={paro} />
        <div className="flex flex-col gap-1.5">
          <Label htmlFor="cerrar-paro-hora">Hora de cierre</Label>
          <Input
            id="cerrar-paro-hora"
            type="datetime-local"
            min={minWall}
            max={nowWall()}
            value={horaFinWall}
            onChange={(e) => setHoraFinWall(e.target.value)}
          />
          <p className="text-[11px] text-fg-tertiary">
            Puede registrar un cierre anterior al momento actual si el paro terminó antes.
          </p>
        </div>
        <div className="flex flex-col gap-1.5">
          <Label htmlFor="cerrar-paro-obs">Observaciones (opcional)</Label>
          <Textarea
            id="cerrar-paro-obs"
            rows={3}
            value={observaciones}
            onChange={(e) => setObservaciones(e.target.value)}
            placeholder="Ej. se ajustó la mordaza y la línea reanudó"
          />
        </div>
        {error ? <p className="rounded bg-crit-soft px-3 py-2 text-xs text-crit">{error}</p> : null}
      </DialogContent>
      <DialogFooter>
        <Button variant="outline" onClick={onClose} disabled={cerrar.isPending}>
          Cancelar
        </Button>
        <Button onClick={() => void handleCerrar()} loading={cerrar.isPending}>
          Cerrar paro
        </Button>
      </DialogFooter>
    </Dialog>
  );
}

// ---------------------------------------------------------------------------
// Modal: revisar paro — flujo UNIFICADO (correcto → validar | corregir →
// propuesta). Recicla CorreccionParoFormulario para no duplicar lógica.
// ---------------------------------------------------------------------------

type CaminoRevision = 'correcto' | 'corregir';

function RevisarParoModal({ paro, onClose }: { paro: ParoMttoItem; onClose: () => void }) {
  const validar = useValidarParoMtto();
  const [camino, setCamino] = useState<CaminoRevision>('correcto');

  async function handleValidar() {
    try {
      await validar.mutateAsync(paro.id);
      toast.success('Paro validado sin cambios');
      onClose();
    } catch (err) {
      // 409 already_validated / correccion_pendiente → mensaje del server.
      toast.error(mensajeError(err));
    }
  }

  return (
    <Dialog open onClose={onClose} className="max-w-lg">
      <DialogHeader>
        <DialogTitle>Revisar paro</DialogTitle>
      </DialogHeader>
      <DialogContent className="flex flex-col gap-3">
        <ResumenParo paro={paro} />
        <SegmentedControl<CaminoRevision>
          value={camino}
          onChange={setCamino}
          size="sm"
          options={[
            { value: 'correcto', label: 'El paro está correcto' },
            { value: 'corregir', label: 'Necesita corrección' },
          ]}
        />
        {camino === 'correcto' ? (
          <p className="text-xs text-fg-secondary">
            Confirme que el registro del líder es correcto tal como está: se sella la validación de
            mantenimiento sin cambios de causal ni de horas.
          </p>
        ) : null}
      </DialogContent>
      {camino === 'correcto' ? (
        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={validar.isPending}>
            Cancelar
          </Button>
          <Button onClick={() => void handleValidar()} loading={validar.isPending}>
            El paro está correcto — validar
          </Button>
        </DialogFooter>
      ) : (
        <CorreccionParoFormulario paro={paro} onClose={onClose} />
      )}
    </Dialog>
  );
}

// ---------------------------------------------------------------------------
// Modal: proponer corrección directa (paros ya validados) — mismo formulario
// ---------------------------------------------------------------------------

function ProponerCorreccionModal({ paro, onClose }: { paro: ParoMttoItem; onClose: () => void }) {
  return (
    <Dialog open onClose={onClose} className="max-w-lg">
      <DialogHeader>
        <DialogTitle>Proponer corrección</DialogTitle>
      </DialogHeader>
      <DialogContent className="pb-0">
        <ResumenParo paro={paro} />
      </DialogContent>
      <CorreccionParoFormulario paro={paro} onClose={onClose} />
    </Dialog>
  );
}

// ---------------------------------------------------------------------------
// Formulario de corrección (causal en cascada + horas + razón + preview del
// diff). Renderiza su propio DialogContent + DialogFooter para reciclarse
// dentro de RevisarParoModal y de ProponerCorreccionModal.
// ---------------------------------------------------------------------------

function CorreccionParoFormulario({ paro, onClose }: { paro: ParoMttoItem; onClose: () => void }) {
  const proponer = useProponerCorreccionParo();
  const causalesQ = useCausalesParo();
  const causales = causalesQ.data ?? [];

  const [causalMadreId, setCausalMadreId] = useState('');
  const [causalId, setCausalId] = useState('');
  const [horaInicioWall, setHoraInicioWall] = useState('');
  const [horaFinWall, setHoraFinWall] = useState('');
  const [razon, setRazon] = useState('');
  const [error, setError] = useState<string | null>(null);

  const madre = causales.find((m) => m.id === causalMadreId);
  const especificas = madre?.hijas ?? [];
  const especifica = especificas.find((c) => c.id === causalId) ?? null;

  const cambiaCausal = causalMadreId !== '' && causalId !== '';
  const cambiaHoras = horaInicioWall !== '' || horaFinWall !== '';
  const hayCambio = cambiaCausal || cambiaHoras;

  // Preview: valores efectivos (propuesto o actual) → minutos resultantes.
  const inicioEfectivo = horaInicioWall ? isoFromWall(horaInicioWall) : paro.hora_inicio;
  const finEfectivo = horaFinWall ? isoFromWall(horaFinWall) : paro.hora_fin;
  const minutosPropuestos = finEfectivo ? minutosEntre(inicioEfectivo, finEfectivo) : null;

  async function handleProponer() {
    setError(null);
    if (causalMadreId !== '' && causalId === '') {
      setError('Elija también la causal específica');
      return;
    }
    if (minutosPropuestos !== null && minutosPropuestos <= 0) {
      setError('La hora fin propuesta debe ser posterior a la hora inicio');
      return;
    }
    const input: CorreccionParoProponer = {
      ...(cambiaCausal ? { causal_madre_id: causalMadreId, causal_id: causalId } : {}),
      ...(horaInicioWall ? { hora_inicio: isoFromWall(horaInicioWall) } : {}),
      ...(horaFinWall ? { hora_fin: isoFromWall(horaFinWall) } : {}),
      razon: razon.trim(),
    };
    const parsed = correccionParoProponerSchema.safeParse(input);
    if (!parsed.success) {
      const primero = parsed.error.errors[0];
      setError(primero ? primero.message : 'Datos inválidos');
      return;
    }
    try {
      await proponer.mutateAsync({ paroId: paro.id, input: parsed.data });
      toast.success('Corrección propuesta — queda pendiente de aprobación del supervisor');
      onClose();
    } catch (err) {
      if (esCodigoApi(err, 'correccion_ya_propuesta')) {
        toast.error('Este paro ya tiene una corrección propuesta sin resolver');
        return;
      }
      toast.error(mensajeError(err));
    }
  }

  return (
    <>
      <DialogContent className="flex flex-col gap-3 pt-0">
        {causalesQ.isLoading ? (
          <p className="flex items-center gap-2 text-xs text-fg-tertiary">
            <Spinner className="size-3.5" /> Cargando causales…
          </p>
        ) : (
          <>
            <div className="flex flex-col gap-1.5">
              <Label htmlFor="corr-madre">Nueva categoría (opcional)</Label>
              <Select
                id="corr-madre"
                value={causalMadreId}
                onChange={(e) => {
                  setCausalMadreId(e.target.value);
                  setCausalId('');
                }}
              >
                <option value="">— Sin cambio de causal —</option>
                {causales.map((m) => (
                  <option key={m.id} value={m.id}>
                    {m.nombre}
                  </option>
                ))}
              </Select>
            </div>
            {causalMadreId !== '' ? (
              <div className="flex flex-col gap-1.5">
                <Label htmlFor="corr-especifica">Nueva causal específica</Label>
                <Select
                  id="corr-especifica"
                  value={causalId}
                  onChange={(e) => setCausalId(e.target.value)}
                >
                  <option value="">— Elija —</option>
                  {especificas.map((c) => (
                    <option key={c.id} value={c.id}>
                      {c.nombre}
                    </option>
                  ))}
                </Select>
              </div>
            ) : null}
          </>
        )}

        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
          <div className="flex flex-col gap-1.5">
            <Label htmlFor="corr-inicio">Nueva hora inicio (opcional)</Label>
            <Input
              id="corr-inicio"
              type="datetime-local"
              value={horaInicioWall}
              onChange={(e) => setHoraInicioWall(e.target.value)}
            />
            <p className="text-[11px] text-fg-tertiary">
              Actual: {fmtFechaHoraParo(paro.hora_inicio)}
            </p>
          </div>
          <div className="flex flex-col gap-1.5">
            <Label htmlFor="corr-fin">Nueva hora fin (opcional)</Label>
            <Input
              id="corr-fin"
              type="datetime-local"
              value={horaFinWall}
              onChange={(e) => setHoraFinWall(e.target.value)}
            />
            <p className="text-[11px] text-fg-tertiary">
              Actual: {fmtFechaHoraParo(paro.hora_fin)}
            </p>
          </div>
        </div>

        <div className="flex flex-col gap-1.5">
          <Label htmlFor="corr-razon">Razón de la corrección</Label>
          <Textarea
            id="corr-razon"
            rows={3}
            value={razon}
            onChange={(e) => setRazon(e.target.value)}
            placeholder="Explique por qué el registro del líder debe corregirse…"
          />
          <p className={cn('text-xs', razon.trim().length >= 10 ? 'text-ok' : 'text-fg-tertiary')}>
            {razon.trim().length} / 10 caracteres mínimos
          </p>
        </div>

        {hayCambio ? (
          <div className="rounded border border-info/30 bg-info/10 p-2.5 text-xs">
            <p className="mb-1 font-medium uppercase tracking-wide text-info">
              Vista previa del cambio
            </p>
            <dl className="flex flex-col gap-1 text-fg-secondary">
              {cambiaCausal && especifica && madre ? (
                <div>
                  <dt className="inline text-fg-tertiary">Causal: </dt>
                  <dd className="inline">
                    {paro.causal_madre.nombre} → {paro.causal.nombre}{' '}
                    <span className="text-fg-tertiary">⇒</span>{' '}
                    <strong className="text-fg">
                      {madre.nombre} → {especifica.nombre}
                    </strong>
                  </dd>
                </div>
              ) : null}
              {horaInicioWall !== '' ? (
                <div>
                  <dt className="inline text-fg-tertiary">Inicio: </dt>
                  <dd className="inline">
                    {fmtHoraParo(paro.hora_inicio)} <span className="text-fg-tertiary">⇒</span>{' '}
                    <strong className="text-fg">{fmtHoraParo(isoFromWall(horaInicioWall))}</strong>
                  </dd>
                </div>
              ) : null}
              {horaFinWall !== '' ? (
                <div>
                  <dt className="inline text-fg-tertiary">Fin: </dt>
                  <dd className="inline">
                    {fmtHoraParo(paro.hora_fin)} <span className="text-fg-tertiary">⇒</span>{' '}
                    <strong className="text-fg">{fmtHoraParo(isoFromWall(horaFinWall))}</strong>
                  </dd>
                </div>
              ) : null}
              {cambiaHoras ? (
                <div>
                  <dt className="inline text-fg-tertiary">Minutos: </dt>
                  <dd className="inline">
                    {fmtMinutosParo(paro.minutos_perdidos)}{' '}
                    <span className="text-fg-tertiary">⇒</span>{' '}
                    <strong className="text-fg">{fmtMinutosParo(minutosPropuestos)}</strong>
                  </dd>
                </div>
              ) : null}
            </dl>
          </div>
        ) : (
          <p className="text-[11px] text-fg-tertiary">
            Proponga al menos un cambio: la causal (madre y específica juntas) o las horas.
          </p>
        )}

        {error ? <p className="rounded bg-crit-soft px-3 py-2 text-xs text-crit">{error}</p> : null}
      </DialogContent>
      <DialogFooter>
        <Button variant="outline" onClick={onClose} disabled={proponer.isPending}>
          Cancelar
        </Button>
        <Button
          onClick={() => void handleProponer()}
          loading={proponer.isPending}
          disabled={!hayCambio || razon.trim().length < 10}
        >
          Proponer corrección
        </Button>
      </DialogFooter>
    </>
  );
}

// ---------------------------------------------------------------------------
// Modal: generar OT desde el paro (crear nueva o ligar una abierta)
// ---------------------------------------------------------------------------

function GenerarOtModal({ paro, onClose }: { paro: ParoMttoItem; onClose: () => void }) {
  const generar = useGenerarOtDesdeParo();
  const ordenesQ = useOrdenesMtto({ estado: 'abierta' });
  const activosQ = useActivosMtto({ linea_id: paro.linea_id });

  const [modo, setModo] = useState<'nueva' | 'existente'>('nueva');
  const [ordenId, setOrdenId] = useState('');
  const [activoId, setActivoId] = useState('');
  const [descripcion, setDescripcion] = useState('');
  const [error, setError] = useState<string | null>(null);

  const activos = activosQ.data ?? [];
  const ordenes = ordenesQ.data ?? [];

  async function handleGenerar() {
    setError(null);
    let input: ParoOrdenTrabajoInput;
    if (modo === 'existente') {
      if (ordenId === '') {
        setError('Elija la orden de trabajo a ligar');
        return;
      }
      input = { orden_trabajo_id: ordenId };
    } else {
      const desc = descripcion.trim();
      if (desc.length > 0 && desc.length < 5) {
        setError('La descripción debe tener mínimo 5 caracteres');
        return;
      }
      if (activoId === '' && activos.length !== 1) {
        setError('Elija el activo a intervenir');
        return;
      }
      input = {
        ...(activoId !== '' ? { activo_id: activoId } : {}),
        ...(desc.length >= 5 ? { descripcion: desc } : {}),
      };
    }
    try {
      const res = await generar.mutateAsync({ paroId: paro.id, input });
      toast.success(
        res.creada
          ? `OT ${res.codigo} creada y ligada al paro`
          : `Paro ligado a la OT ${res.codigo}`,
      );
      onClose();
    } catch (err) {
      toast.error(mensajeError(err));
    }
  }

  return (
    <Dialog open onClose={onClose}>
      <DialogHeader>
        <DialogTitle>Generar orden de trabajo</DialogTitle>
      </DialogHeader>
      <DialogContent className="flex flex-col gap-3">
        <ResumenParo paro={paro} />

        <SegmentedControl<'nueva' | 'existente'>
          value={modo}
          onChange={(v) => {
            setModo(v);
            setError(null);
          }}
          size="sm"
          options={[
            { value: 'nueva', label: 'Crear OT nueva' },
            { value: 'existente', label: 'Ligar OT abierta' },
          ]}
        />

        {modo === 'existente' ? (
          <div className="flex flex-col gap-1.5">
            <Label htmlFor="ot-paro-existente">Orden de trabajo abierta</Label>
            <Select
              id="ot-paro-existente"
              value={ordenId}
              disabled={ordenesQ.isLoading}
              onChange={(e) => setOrdenId(e.target.value)}
            >
              <option value="">
                {ordenesQ.isLoading ? 'Cargando órdenes…' : '— Elija una orden —'}
              </option>
              {ordenes.map((o) => (
                <option key={o.id} value={o.id}>
                  {o.codigo ? `${o.codigo} · ` : ''}
                  {o.descripcion.length > 60 ? `${o.descripcion.slice(0, 60)}…` : o.descripcion}
                </option>
              ))}
            </Select>
            {!ordenesQ.isLoading && ordenes.length === 0 ? (
              <p className="text-[11px] text-fg-tertiary">
                No hay órdenes abiertas — cree una nueva.
              </p>
            ) : null}
          </div>
        ) : (
          <>
            <div className="flex flex-col gap-1.5">
              <Label htmlFor="ot-paro-activo">Activo</Label>
              <Select
                id="ot-paro-activo"
                value={activoId}
                disabled={activosQ.isLoading}
                onChange={(e) => setActivoId(e.target.value)}
              >
                <option value="">
                  {activosQ.isLoading
                    ? 'Cargando activos…'
                    : activos.length === 1
                      ? 'Automático (único activo de la línea)'
                      : '— Elija el activo —'}
                </option>
                {activos.map((a) => (
                  <option key={a.id} value={a.id}>
                    {a.codigo ? `${a.codigo} · ` : ''}
                    {a.nombre}
                  </option>
                ))}
              </Select>
              {!activosQ.isLoading && activos.length === 0 ? (
                <p className="text-[11px] text-warn">
                  La línea no tiene activos registrados — elija el activo en el catálogo o solicite
                  su creación.
                </p>
              ) : null}
            </div>
            <div className="flex flex-col gap-1.5">
              <Label htmlFor="ot-paro-desc">Descripción (opcional)</Label>
              <Textarea
                id="ot-paro-desc"
                rows={3}
                value={descripcion}
                onChange={(e) => setDescripcion(e.target.value)}
                placeholder="Si la deja vacía se genera desde la causal del paro"
              />
            </div>
          </>
        )}

        {error ? <p className="rounded bg-crit-soft px-3 py-2 text-xs text-crit">{error}</p> : null}
      </DialogContent>
      <DialogFooter>
        <Button variant="outline" onClick={onClose} disabled={generar.isPending}>
          Cancelar
        </Button>
        <Button onClick={() => void handleGenerar()} loading={generar.isPending}>
          {modo === 'existente' ? 'Ligar OT' : 'Crear OT'}
        </Button>
      </DialogFooter>
    </Dialog>
  );
}

// ---------------------------------------------------------------------------
// Card de un paro dentro del drawer — TODO el detalle
// ---------------------------------------------------------------------------

function ParoCard({ paro, puedeOperar }: { paro: ParoMttoItem; puedeOperar: boolean }) {
  const turnoLabel =
    paro.turno_nombre ?? (paro.turno_numero !== null ? `T${paro.turno_numero}` : '—');
  return (
    <Card variant={paro.estado_mtto === 'abierto' ? 'crit-left' : 'default'}>
      <CardContent className="flex flex-col gap-2 p-4 pt-3 text-sm">
        <div className="flex flex-wrap items-start justify-between gap-2">
          <div className="min-w-0">
            <p className="font-medium text-fg">
              {paro.causal_madre.nombre} → {paro.causal.nombre}
            </p>
            <p className="mt-0.5 text-[11px] text-fg-tertiary">
              {fmtFecha(paro.fecha)} · Turno {turnoLabel}
            </p>
          </div>
          <EstadoParoMttoBadge estado={paro.estado_mtto} />
        </div>

        <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-xs text-fg-secondary">
          <span className="tabular-nums">
            {fmtHoraParo(paro.hora_inicio)} —{' '}
            {paro.hora_fin === null ? (
              <strong className="text-crit">en curso</strong>
            ) : (
              fmtHoraParo(paro.hora_fin)
            )}
          </span>
          <span className="font-medium tabular-nums text-fg">
            {fmtMinutosParo(paro.minutos_perdidos)}
          </span>
          <Chip
            tone={paro.categoria.es_mantenimiento ? 'accent' : 'neutral'}
            className="px-2 py-0.5 text-[11px]"
          >
            {paro.categoria.nombre}
          </Chip>
        </div>

        <p className="text-[11px] text-fg-tertiary">
          Registrado por:{' '}
          {paro.lider ? (
            <span className="text-fg-secondary">
              {paro.lider.nombre} {paro.lider.apellido}
            </span>
          ) : (
            'sin líder asociado'
          )}
          {paro.cerrado_por_mtto ? ' · cierre hecho por mantenimiento' : ''}
          {paro.validado_mtto_at ? ` · validado el ${fmtFechaHoraParo(paro.validado_mtto_at)}` : ''}
        </p>

        {paro.observaciones ? (
          <p className="rounded bg-surface-overlay px-2 py-1.5 text-xs italic text-fg-secondary">
            “{paro.observaciones}”
          </p>
        ) : null}

        {paro.correccion_viva ? (
          <p className="rounded border border-info/30 bg-info/10 px-2 py-1.5 text-[11px] text-fg-secondary">
            <strong className="text-info">Corrección propuesta</strong> el{' '}
            {fmtFechaHoraParo(paro.correccion_viva.created_at)}: {paro.correccion_viva.razon}
          </p>
        ) : null}

        <ParoAcciones paro={paro} puedeOperar={puedeOperar} />
      </CardContent>
    </Card>
  );
}

// ---------------------------------------------------------------------------
// Drawer — paros del día operativo de la línea seleccionada
// ---------------------------------------------------------------------------

export interface LineaSeleccionada {
  id: string;
  nombre: string;
}

export function ParoDetalleDrawer({
  linea,
  onClose,
}: {
  linea: LineaSeleccionada | null;
  onClose: () => void;
}) {
  const { puedeOperar } = usePermisosMtto();
  const parosQ = useLineaParosDia(linea?.id ?? null);
  const data = parosQ.data;

  return (
    <Drawer open={linea !== null} onClose={onClose} width={560}>
      <DrawerHeader>
        <div className="min-w-0">
          <DrawerTitle>Paros del día · {data?.linea.nombre ?? linea?.nombre ?? ''}</DrawerTitle>
          <p className="mt-0.5 text-[11px] text-fg-tertiary">
            {data
              ? `${data.linea.planta_nombre} · ${fmtFecha(data.fecha)}`
              : 'Día operativo actual'}
          </p>
        </div>
        <Button variant="ghost" size="sm" onClick={onClose}>
          Cerrar ✕
        </Button>
      </DrawerHeader>
      <DrawerBody className="flex flex-col gap-3">
        {parosQ.isLoading ? <CargandoModulo mensaje="Cargando paros de la línea…" /> : null}
        {parosQ.isError ? <ErrorModulo error={parosQ.error} /> : null}

        {data && data.paros.length === 0 ? (
          <p className="py-10 text-center text-sm text-fg-tertiary">
            Sin paros registrados para esta línea en el día operativo.
          </p>
        ) : null}

        {data && data.paros.length > 0 ? (
          <>
            {!puedeOperar ? (
              <p className="text-[11px] text-fg-tertiary">
                Su usuario puede consultar los paros; para operarlos necesita un rol de
                mantenimiento.
              </p>
            ) : null}
            {data.paros.map((p) => (
              <ParoCard key={p.id} paro={p} puedeOperar={puedeOperar} />
            ))}
          </>
        ) : null}
      </DrawerBody>
    </Drawer>
  );
}
